import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { http } from '../lib/api';

/** Exchanges a permission-checked document URL for its short-lived signed storage URL (the API needs the bearer token). */
export async function fetchSignedUrl(src: string, disposition?: 'inline'): Promise<string> {
  const res = await http.get<{ data: { url: string } }>(src, {
    params: { mode: 'url', ...(disposition ? { disposition } : {}) },
  });
  // Local-storage URLs are relative to the API host, not the web app.
  return new URL(res.data.data.url, new URL(src, window.location.origin)).toString();
}

/**
 * Image behind a permission-checked document URL. The API needs the bearer token, which a plain <img> cannot send,
 * so the signed storage URL is fetched first and then used as the image source.
 */
export function SecureImage({
  src,
  alt = '',
  className,
  fallback,
}: {
  src: string;
  alt?: string;
  className?: string;
  fallback: ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  const { data } = useQuery({
    queryKey: ['secure-image', src],
    queryFn: () => fetchSignedUrl(src),
    staleTime: 4 * 60_000,
    retry: false,
  });
  if (!data || failed) return <>{fallback}</>;
  return <img src={data} alt={alt} className={className} onError={() => setFailed(true)} />;
}
